import { Link } from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { useAuth, useCan, useIsAdmin } from "@/shared/auth";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";

import {
  useActivity,
  useAddNote,
  useDeleteNote,
  useRawPayload,
  type ActivityEvent,
  type ActivitySource,
} from "../api/use-activity";
import { isSystemKind } from "../lib/activity-kind";
import { auditNavigationForOrder } from "../lib/audit-link";

import { ActivityRow } from "./ActivityRow";
import { PayloadDrawer } from "./PayloadDrawer";

interface OrderActivityProps {
  orderId: string;
}

const SOURCES: readonly ActivitySource[] = ["marketplace", "warehouse", "atlas", "people"];

/** Notes are less than 5 minutes old before their own author may delete them (backend-enforced too). */
const NOTE_DELETE_WINDOW_MS = 5 * 60 * 1000;

const BANGKOK_TZ = "Asia/Bangkok";
const bkkDateKeyFormatter = new Intl.DateTimeFormat("en-CA", { timeZone: BANGKOK_TZ });

function dayKey(d: Date): string {
  return bkkDateKeyFormatter.format(d);
}

/** Groups items (already newest-first from the API) into ordered day buckets, labeling today/yesterday. */
function groupByDay(
  items: ActivityEvent[],
  labelFor: (key: string) => string,
): { key: string; label: string; items: ActivityEvent[] }[] {
  const groups: { key: string; label: string; items: ActivityEvent[] }[] = [];
  for (const item of items) {
    const key = dayKey(new Date(item.at));
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, label: labelFor(key), items: [item] });
    }
  }
  return groups;
}

export function OrderActivity({ orderId }: OrderActivityProps): JSX.Element {
  const { t } = useTranslation("orders");
  const { user } = useAuth();
  const [source, setSource] = useState<ActivitySource | undefined>(undefined);
  const [noteText, setNoteText] = useState("");
  const [rawOpen, setRawOpen] = useState(false);
  const [showSystem, setShowSystem] = useState(false);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useActivity(
    orderId,
    source,
  );
  const addNote = useAddNote(orderId);
  const deleteNote = useDeleteNote(orderId);
  const raw = useRawPayload(orderId, rawOpen);

  const items = data?.pages.flatMap((p) => p.items ?? []) ?? [];
  const systemCount = items.filter((i) => isSystemKind(i.kind)).length;
  const visibleItems = showSystem ? items : items.filter((i) => !isSystemKind(i.kind));
  const counts = data?.pages[0]?.counts ?? {};
  const totalCount = SOURCES.reduce((sum, s) => sum + (counts[s] ?? 0), 0);

  const isAdmin = useIsAdmin();
  const canWriteNotes = useCan("orders:write");

  const now = new Date();
  const todayKey = dayKey(now);
  const yesterdayKey = dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  function labelFor(key: string): string {
    if (key === todayKey) return t("activity.today");
    if (key === yesterdayKey) return t("activity.yesterday");
    return key;
  }
  const groups = groupByDay(visibleItems, labelFor);

  function canDeleteNote(item: ActivityEvent): boolean {
    if (!canWriteNotes) return false;
    if (item.kind !== "people.note" || item.audit_event_id === undefined) return false;
    if (item.actor.name !== user?.display_name) return false;
    return now.getTime() - new Date(item.at).getTime() < NOTE_DELETE_WINDOW_MS;
  }

  function handlePost(): void {
    const text = noteText.trim();
    if (!text) return;
    addNote.mutate(text, {
      onSuccess: () => setNoteText(""),
      onError: (e) => toast.error(apiErrorMessage(e)),
    });
  }

  return (
    <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
        <h3 className="font-semibold text-[var(--text-primary)]">{t("activity.title")}</h3>
        {isAdmin && (
          <Link
            to={auditNavigationForOrder(orderId).to}
            search={auditNavigationForOrder(orderId).search}
            className="ml-auto text-xs font-medium text-[var(--brand-600)] hover:underline"
          >
            {t("activity.view_all")} ↗
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-[var(--premium-border)] px-5 py-2.5">
        <button
          type="button"
          onClick={() => setSource(undefined)}
          data-testid="activity-chip-all"
          aria-pressed={source === undefined}
          className={
            "rounded-full px-2.5 py-1 text-xs font-semibold " +
            (source === undefined
              ? "bg-[var(--intent-info-bg)] text-[var(--intent-info-fg)]"
              : "bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]")
          }
        >
          {t("activity.all")} ({totalCount})
        </button>
        {SOURCES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSource(s)}
            data-testid={`activity-chip-${s}`}
            aria-pressed={source === s}
            className={
              "rounded-full px-2.5 py-1 text-xs font-semibold " +
              (source === s
                ? "bg-[var(--intent-info-bg)] text-[var(--intent-info-fg)]"
                : "bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]")
            }
          >
            {t(`activity.src.${s}`)} ({counts[s] ?? 0})
          </button>
        ))}
        {systemCount > 0 && (
          <button
            type="button"
            data-testid="show-system-events-toggle"
            className="px-3 py-1.5 text-xs text-[var(--text-subtle)] underline"
            onClick={() => setShowSystem((s) => !s)}
          >
            {showSystem
              ? t("activity.hide_system_events")
              : t("activity.show_system_events", { n: systemCount })}
          </button>
        )}
      </div>

      {canWriteNotes && (
        <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
          <input
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t("activity.add_note")}
            aria-label={t("activity.add_note")}
            data-testid="activity-note-input"
            maxLength={2000}
            className="min-w-0 flex-1 rounded-lg border border-[var(--premium-border)] px-3 py-1.5 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handlePost}
            disabled={!noteText.trim() || addNote.isPending}
            data-testid="activity-note-post"
          >
            {t("activity.post")}
          </Button>
        </div>
      )}

      <div className="px-5">
        {isError && (
          <p data-testid="activity-error" className="py-6 text-sm text-[var(--intent-danger-fg)]">
            {t("activity.error")}
          </p>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <p className="py-6 text-sm text-[var(--text-subtle)]">{t("activity.empty")}</p>
        )}
        {!isLoading && !isError && items.length > 0 && visibleItems.length === 0 && (
          // Every loaded row is a collapsed system event: say so, or the card
          // reads as empty while "Show older" is offered (review I7).
          <p data-testid="activity-only-system" className="py-6 text-sm text-[var(--text-subtle)]">
            {t("activity.only_system", { n: systemCount })}
          </p>
        )}
        {groups.map((group) => (
          <div key={group.key}>
            <div className="pt-3 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {group.label}
            </div>
            <div className="divide-y divide-[var(--premium-border)]">
              {group.items.map((item, i) => (
                <ActivityRow
                  key={item.audit_event_id ?? item.stock_audit_id ?? `${group.key}-${i}`}
                  item={item}
                  orderId={orderId}
                  canDelete={canDeleteNote(item)}
                  onDelete={() => {
                    if (item.audit_event_id !== undefined) {
                      deleteNote.mutate(item.audit_event_id, {
                        onError: (e) => toast.error(apiErrorMessage(e)),
                      });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {hasNextPage && (
        <div className="border-t border-[var(--premium-border)] px-5 py-3">
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            data-testid="activity-show-older"
            className="text-xs font-semibold text-[var(--brand-600)] hover:underline"
          >
            {/* `counts` totals the whole feed per source (not just loaded pages), so
                remaining = that total minus what's loaded so far — an exact count, not a guess. */}
            {t("activity.older", {
              n: Math.max((source ? (counts[source] ?? 0) : totalCount) - items.length, 0),
            })}
          </button>
        </div>
      )}

      {canWriteNotes && items.some((i) => i.raw_available) && (
        <div className="border-t border-[var(--premium-border)] px-5 py-3">
          <button
            type="button"
            data-testid="view-raw-payload-link"
            className="text-xs font-semibold text-[var(--brand-600)] hover:underline"
            onClick={() => setRawOpen(true)}
          >
            {t("activity.view_raw_payload")}
          </button>
        </div>
      )}

      <PayloadDrawer raw={raw.data} open={rawOpen} onClose={() => setRawOpen(false)} />
    </div>
  );
}
